// @mui
import { Alert, Box, Chip, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { GetMedicineDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';

// ----------------------------------------------------------------------

interface MedicineDetailsPanelProps {
  medicine: GetMedicineDto | null;
  /** Facility forms (#690): what the Warehouse holds, under the manufacturer's data. */
  stockSummary?: React.ReactNode;
}

/**
 * Read-only panel that shows preparationType / strength / pharmaceuticalForm /
 * licenseNumber / responsibleEntity / characteristicUrl for a selected medicine
 * product from the database, then an optional stock block.
 *
 * Renders nothing when there is neither a medicine nor a stock block.
 */
export default function MedicineDetailsPanel({
  medicine,
  stockSummary,
}: MedicineDetailsPanelProps) {
  if (!medicine && !stockSummary) return null;

  return (
    <Alert
      severity="info"
      sx={{
        mb: 1,
        backgroundColor: '#E3F6FF',
      }}
      icon={false}
    >
      {medicine && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translateKey('hardcoded.szczegoly_leku')}
          </Typography>
          <Stack spacing={0.5}>
            {medicine.preparationType && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Typ preparatu:
                </Typography>{' '}
                <Chip
                  label={
                    medicine.preparationType === 'Ludzki'
                      ? 'Ludzki'
                      : 'Weterynaryjny'
                  }
                  size="small"
                  color={
                    medicine.preparationType === 'Ludzki' ? 'info' : 'success'
                  }
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    ml: 0.5,
                    ...(medicine.preparationType !== 'Ludzki' && {
                      backgroundColor: 'secondary.main',
                      color: 'text.primary',
                    }),
                  }}
                />
              </Box>
            )}
            {medicine.strength && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Moc:
                </Typography>{' '}
                <Typography variant="caption">{medicine.strength}</Typography>
              </Box>
            )}
            {medicine.pharmaceuticalForm && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {translateKey('hardcoded.postac_farmaceutyczna')}
                </Typography>{' '}
                <Typography variant="caption">
                  {medicine.pharmaceuticalForm}
                </Typography>
              </Box>
            )}
            {medicine.licenseNumber && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Numer pozwolenia:
                </Typography>{' '}
                <Typography variant="caption">
                  {medicine.licenseNumber}
                </Typography>
              </Box>
            )}
            {medicine.responsibleEntity && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Podmiot odpowiedzialny:
                </Typography>{' '}
                <Typography variant="caption">
                  {medicine.responsibleEntity}
                </Typography>
              </Box>
            )}
            {medicine.characteristicUrl && (
              <Box sx={{ mt: 1 }}>
                <Link
                  href={medicine.characteristicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ fontWeight: 600 }}
                >
                  Charakterystyka produktu leczniczego (PDF)
                </Link>
              </Box>
            )}
          </Stack>
        </>
      )}
      {stockSummary && <Box sx={{ mt: medicine ? 1.5 : 0 }}>{stockSummary}</Box>}
    </Alert>
  );
}
